<template>
    <v-dialog :value="show" max-width="1100" persistent @input="onDialogInput">
        <v-card>
            <v-toolbar color="primary" dark>
                <v-toolbar-title>Monitoramento de Balanceamento</v-toolbar-title>
                <v-spacer />
                <v-btn icon @click="close">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
                <v-layout wrap>
                    <v-flex xs12>
                        <v-alert type="info" outlined dense>
                            Grupo: <strong>{{ groupName }}</strong>
                            <span v-if="group && group.server"> | Servidor: <strong>{{ group.server }}</strong></span>
                        </v-alert>
                    </v-flex>

                    <v-flex xs12 v-if="hasNoData">
                        <v-alert type="error" outlined dense>
                            A API não retornou dados de monitoramento para este grupo. Verifique se o grupo está
                            configurado corretamente no servidor.
                        </v-alert>
                    </v-flex>

                    <v-flex xs12 v-if="hasValidationData && validationIssues.length">
                        <v-alert type="warning" outlined dense>
                            Foram encontradas divergências entre o grupo salvo e o retorno da API:
                            <div v-for="issue in validationIssues" :key="issue">- {{ issue }}</div>
                        </v-alert>
                    </v-flex>

                    <v-flex xs12 v-if="monitor.stabilizing">
                        <v-alert type="warning" outlined dense icon="hourglass_empty">
                            Grupo em janela de estabilização. Corte de potência liberado em <strong>{{ holdCountdown }}</strong>.
                            Um carro conectou ou desconectou recentemente; o corte de potência está temporariamente
                            suspenso para os veículos negociarem livremente a potência necessária.
                        </v-alert>
                    </v-flex>

                    <v-flex xs12 md4>
                        <v-card outlined class="pa-3 mb-3">
                            <div class="text-caption">Potência Máxima do Grupo</div>
                            <div class="text-h6">{{ monitor.maxPower || 0 }} kW</div>
                        </v-card>
                    </v-flex>

                    <v-flex xs12 md4>
                        <v-card outlined class="pa-3 mb-3">
                            <div class="text-caption">Potência em Uso</div>
                            <div class="text-h6">{{ usedPowerRounded }} kW</div>
                        </v-card>
                    </v-flex>

                    <v-flex xs12 md4>
                        <v-card outlined class="pa-3 mb-3">
                            <div class="text-caption">Utilização</div>
                            <div class="text-h6" :class="{ 'red--text': isOverLimit }">{{ usagePercent }}%</div>
                        </v-card>
                    </v-flex>

                    <v-flex xs12>
                        <v-progress-linear :value="usagePercent" :color="isOverLimit ? 'error' : 'success'" height="12" rounded class="mb-4" />
                    </v-flex>

                    <v-flex xs12>
                        <v-data-table :headers="headers" :items="rows" :items-per-page="10" class="elevation-1"
                            :loading="loading" loading-text="Carregando monitoramento...">
                            <template v-slot:item.chargerPlug="{ item }">
                                <v-btn small text color="primary" :href="href(`/now/${item.charger}/${item.plug}`)"
                                    target="_blank" :title="'Último ping: ' + formatDateTime(item.lastPingAt)">
                                    {{ item.charger }} - {{ item.plug }}
                                </v-btn>
                            </template>

                            <template v-slot:item.currentPower="{ item }">
                                {{ formatPower(item.currentPower) }} kW
                            </template>

                            <template v-slot:item.power="{ item }">
                                <span v-if="item.lastSavedPower === null || item.lastSavedPower === undefined" class="grey--text">
                                    Não configurado
                                </span>
                                <span v-else>{{ formatPower(item.lastSavedPower) }} kW</span>
                            </template>

                            <template v-slot:item.status="{ item }">
                                <v-chip small :color="statusColor(item)" dark>
                                    {{ (item.status || 'Sem status') + (item.online ? '' : ' - Offline') }}
                                </v-chip>
                            </template>

                            <template v-slot:item.sync="{ item }">
                                <v-chip v-if="item.releasePending" small color="warning" dark>
                                    <v-icon small left>bolt</v-icon>
                                    Liberando potência
                                </v-chip>
                                <v-chip v-else-if="item.pendingPowerSave" small color="info" dark>
                                    <v-icon small left>sync</v-icon>
                                    Sincronizando...
                                </v-chip>
                                <v-chip v-else-if="item.online" small color="success" outlined>
                                    Sincronizado
                                </v-chip>
                                <span v-else>-</span>
                            </template>
                        </v-data-table>
                    </v-flex>
                </v-layout>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn text color="primary" :loading="loading" @click="fetchMonitor">Atualizar agora</v-btn>
                <v-btn color="primary" @click="close">Fechar</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        group: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            loading: false,
            timer: null,
            tickTimer: null,
            now: Date.now(),
            monitor: {
                name: null,
                maxPower: 0,
                usedPower: 0,
                chargers: [],
            },
            headers: [
                { text: 'Carregador / Plug', value: 'chargerPlug', sortable: true },
                { text: 'Status', value: 'status', sortable: false },
                { text: 'Potência Real', value: 'currentPower', sortable: true },
                { text: 'Perfil de Carregamento', value: 'power', sortable: true },
                { text: 'Sincronização', value: 'sync', sortable: false },
            ],
        };
    },

    computed: {
        groupName() {
            return this.group && this.group.name ? this.group.name : 'N/A';
        },

        expectedMaxPower() {
            return Number(this.group?.config?.maxPower || 0);
        },

        expectedChargers() {
            const chargers = this.group?.config?.chargers;
            return Array.isArray(chargers) ? chargers.map((charger) => String(charger)) : [];
        },

        monitorChargers() {
            const chargers = this.monitor?.chargers;
            return Array.isArray(chargers) ? chargers.map((charger) => String(charger.name)) : [];
        },

        hasNoData() {
            return Boolean(this.show && !this.loading && (!this.monitor || !this.monitor.name));
        },

        hasValidationData() {
            return Boolean(this.group && this.group.name && this.monitor && this.monitor.name);
        },

        validationIssues() {
            if (!this.hasValidationData) return [];

            const issues = [];
            const expectedGroupName = String(this.group.name);
            const receivedGroupName = String(this.monitor.name || '');

            if (receivedGroupName !== expectedGroupName) {
                issues.push(`Nome do grupo divergente: esperado ${expectedGroupName}, recebido ${receivedGroupName || 'vazio'}.`);
            }

            if (Number(this.monitor.maxPower || 0) !== this.expectedMaxPower) {
                issues.push(`Potência máxima divergente: esperado ${this.expectedMaxPower} kW, recebido ${Number(this.monitor.maxPower || 0)} kW.`);
            }

            const missingChargers = this.expectedChargers.filter((chargerName) => !this.monitorChargers.includes(chargerName));
            const unexpectedChargers = this.monitorChargers.filter((chargerName) => !this.expectedChargers.includes(chargerName));

            if (missingChargers.length) {
                issues.push(`Carregadores ausentes no monitoramento: ${missingChargers.join(', ')}.`);
            }

            if (unexpectedChargers.length) {
                issues.push(`Carregadores inesperados no monitoramento: ${unexpectedChargers.join(', ')}.`);
            }

            return issues;
        },

        usedPowerRounded() {
            return this.formatPower(this.monitor.usedPower || 0);
        },

        usagePercent() {
            if (!this.monitor.maxPower) return 0;
            const percent = (Number(this.monitor.usedPower || 0) / Number(this.monitor.maxPower || 0)) * 100;
            return Math.max(0, Number(percent.toFixed(1)));
        },

        isOverLimit() {
            return this.usagePercent > 100;
        },

        holdCountdown() {
            if (!this.monitor.holdUntil) return '-';
            const remainingMs = new Date(this.monitor.holdUntil).getTime() - this.now;
            if (remainingMs <= 0) return '00:00';

            const totalSeconds = Math.floor(remainingMs / 1000);
            const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
            const seconds = String(totalSeconds % 60).padStart(2, '0');
            return `${minutes}:${seconds}`;
        },

        rows() {
            if (!this.monitor || !Array.isArray(this.monitor.chargers)) return [];

            return this.monitor.chargers.flatMap((charger) => {
                const plugs = Array.isArray(charger.plugs) ? charger.plugs : [];
                return plugs.map((plugData) => ({
                    charger: charger.name,
                    chargerPlug: `${charger.name} - ${plugData.plug}`,
                    online: Boolean(charger.online),
                    lastPingAt: charger.lastPingAt,
                    plug: plugData.plug,
                    status: plugData.status,
                    currentPower: plugData.currentPower,
                    power: plugData.power,
                    targetPower: plugData.targetPower,
                    pendingPowerSave: Boolean(plugData.pendingPowerSave),
                    releasePending: Boolean(plugData.releasePending),
                    lastSavedPower: plugData.lastSavedPower,
                }));
            });
        },
    },

    watch: {
        show(newVal) {
            if (newVal) {
                this.fetchMonitor();
                this.startAutoRefresh();
            } else {
                this.stopAutoRefresh();
            }
        },

        group() {
            if (this.show) {
                this.fetchMonitor();
            }
        },
    },

    methods: {
        onDialogInput(val) {
            if (!val) this.close();
        },

        close() {
            this.stopAutoRefresh();
            this.$emit('close');
        },

        startAutoRefresh() {
            this.stopAutoRefresh();
            this.timer = setInterval(() => {
                this.fetchMonitor(false);
            }, 10000);
            this.tickTimer = setInterval(() => {
                this.now = Date.now();
            }, 1000);
        },

        stopAutoRefresh() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
            if (this.tickTimer) {
                clearInterval(this.tickTimer);
                this.tickTimer = null;
            }
        },

        href(url) {
            return `${window.location.origin}${url}`;
        },

        statusColor(item) {
            if (!item.online) return 'grey';
            if (item.status === 'Charging') return 'warning';
            return item.status ? 'success' : 'grey';
        },

        formatPower(power) {
            return Number(power || 0).toFixed(2);
        },

        formatDateTime(value) {
            if (!value) return '-';
            return new Date(value).toLocaleString('pt-BR');
        },

        fetchMonitor(showLoading = true) {
            if (!this.group || !this.group.name) {
                this.monitor = { name: null, maxPower: 0, usedPower: 0, chargers: [] };
                return;
            }

            if (!this.group.server) {
                this.$eventHub.$emit('msgError', 'Servidor do grupo não encontrado para monitoramento.');
                return;
            }

            if (showLoading) this.loading = true;

            this.$http
                .getDynamic(this.group.server, `api/v2/loadBalance/${encodeURIComponent(this.group.name)}`)
                .then((result) => {
                    this.monitor = result || { name: null, maxPower: 0, usedPower: 0, chargers: [] };
                })
                .catch((error) => {
                    this.$eventHub.$emit('msgError', error.message ? error.message : 'Erro ao consultar monitoramento do grupo.');
                })
                .finally(() => {
                    if (showLoading) this.loading = false;
                });
        },
    },

    beforeDestroy() {
        this.stopAutoRefresh();
    },
};
</script>
